import { motion } from "framer-motion";
import { MapPin, Play, Camera, Coffee, StickyNote, Square, Send, FileText, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Section";

const JOB = { name: "Water Damage Ceiling Repair", client: "Metro Strata Management", where: "Unit 8, Frankston VIC", worker: "Noah Williams" };
const EVENTS = [
  ["8:06am", MapPin, "Checked in on site", "Location saved against the job address"],
  ["8:10am", Play, "Clocked on", "Shift timer started"],
  ["8:30am", Camera, "Issue photo", "Damaged ceiling on arrival"],
  ["11:30am", Coffee, "Break", "30 min, tracked automatically"],
  ["12:55pm", StickyNote, "Job note", "“Replaced a 2.4m x 1.2m section. Leak already fixed.”"],
  ["1:20pm", Square, "Clocked off", "5h 10m on the job"],
  ["1:26pm", Send, "Report sent", "Emailed to Metro Strata Management"],
];
const PHOTOS = [
  ["/demo/issue_ceiling.jpg", "Issue", "8:30am", "Damaged ceiling on arrival"],
  ["/demo/issue_peeling.jpg", "Before", "9:25am", "Mould behind the paint"],
  ["/demo/after_room3.jpg", "Completed", "12:58pm", "Bathroom ready for paint"],
];
export const SAMPLE_REPORT = "/sample-job-report.pdf";

const Label = ({ k, children }) => (
  <p className="font-mono text-[11px] font-semibold tracking-[0.18em] uppercase text-[#EA580C]">{k} · {children}</p>
);

const Timeline = () => (
  <div className="h-full rounded-2xl bg-[#0B1120] text-white p-7 sm:p-8 home-noise" data-testid="home-proof-timeline">
    <Label k="A">Clock-ins &amp; notes</Label>
    <h3 className="font-display mt-3 text-xl sm:text-2xl font-bold tracking-tight">{JOB.name}</h3>
    <p className="mt-1 text-sm text-slate-400">{JOB.client} · {JOB.where} · {JOB.worker}</p>
    <ol className="mt-7 relative border-l border-white/10 ml-[80px] space-y-5">
      {EVENTS.map(([t, Icon, title, text], i) => (
        <motion.li key={title} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.6 }}
          transition={{ delay: 0.1 * i, duration: 0.4 }} className="relative z-10 pl-6" data-testid={`home-proof-event-${i}`}>
          <span className="absolute -left-[80px] top-0.5 w-[60px] text-right font-mono text-xs text-slate-400">{t}</span>
          <span className="absolute -left-[13px] top-0 w-[26px] h-[26px] rounded-full bg-[#1E293B] border border-white/15 grid place-items-center text-[#EA580C]"><Icon className="w-3.5 h-3.5" /></span>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs sm:text-sm text-slate-400 mt-0.5">{text}</p>
        </motion.li>
      ))}
    </ol>
  </div>
);

const Photos = () => (
  <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7" data-testid="home-proof-photos">
    <Label k="B">Photos from the crew</Label>
    <div className="mt-5 grid grid-cols-3 gap-3">
      {PHOTOS.map(([src, kind, t, cap], i) => (
        <figure key={src} className="group" data-testid={`home-proof-photo-${i}`}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
            <img src={src} alt={cap} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="absolute top-2 left-2 rounded-full bg-[#0B1120]/85 px-2 py-0.5 text-[10px] font-bold text-white">{kind}</span>
          </div>
          <figcaption className="mt-2 text-xs text-slate-600 leading-snug"><span className="font-mono text-slate-400">{t}</span><br />{cap}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

const Report = () => (
  <div className="rounded-2xl bg-[#FFEDD5] border border-[#FED7AA] p-6 sm:p-7 grid sm:grid-cols-[1fr_190px] gap-6 items-center overflow-hidden" data-testid="home-proof-report">
    <div>
      <Label k="C">The client report</Label>
      <h3 className="font-display mt-3 text-xl sm:text-2xl font-bold tracking-tight">One branded PDF, sent in one tap.</h3>
      <p className="mt-2 text-sm sm:text-base text-slate-700">Times on and off site, the crew&apos;s notes and their photos, with your logo and colours. This one came straight out of our demo company.</p>
      <a href={SAMPLE_REPORT} target="_blank" rel="noreferrer" data-testid="home-proof-report-btn"
        className="group mt-5 inline-flex items-center gap-2 rounded-full bg-[#0B1120] px-5 py-3 text-sm font-bold text-white hover:bg-[#EA580C] transition-colors">
        <FileText className="w-4 h-4" /> Open the sample report <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </a>
    </div>
    <a href={SAMPLE_REPORT} target="_blank" rel="noreferrer" aria-label="Sample client report preview" data-testid="home-proof-report-preview"
      className="block mx-auto w-[170px] sm:w-full rotate-2 hover:rotate-0 transition-transform duration-300">
      <img src="/sample-job-report.png" alt="First page of a sample job completion report" className="w-full rounded-md shadow-2xl ring-1 ring-black/5" />
    </a>
  </div>
);

export const ProofOfWork = () => (
  <section id="proof" className="scroll-mt-16 bg-[#F8FAFC] home-grid-light py-24 lg:py-32" data-testid="home-proof">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHead num="01" eyebrow="Proof of work" title={<>“Were they even there?”<br />Now you can show them.</>}
        sub="Every job keeps its own record: when the crew checked in and clocked on, what they noted, the photos they took and the report your client received." />
      <div className="mt-16 grid lg:grid-cols-12 gap-5">
        <Reveal className="lg:col-span-5"><Timeline /></Reveal>
        <div className="lg:col-span-7 grid gap-5">
          <Reveal delay={0.08}><Photos /></Reveal>
          <Reveal delay={0.16}><Report /></Reveal>
        </div>
      </div>
    </div>
  </section>
);
